import type { ChartKind, ValueFormat } from './reports.js';

/**
 * اللوحات الجاهزة.
 *
 * تعريفاتها هنا لا في الواجهة لسبب واحد: مفاتيح المصادر والتقسيمات نصوص
 * لا يفحصها المترجم، وخطأ حرف فيها لا يظهر إلا بطاقةً فارغة على الشاشة —
 * وقد وقع هذا فعلًا في "ownerId" بدل "owner". وجودها بجوار الكتالوج يجعل
 * اختبارًا واحدًا يمرّرها كلها على المحرّك ويكشف أي مفتاح لا يطابقه.
 */

export interface DashboardDefinition {
  source: string;
  dimension: string;
  measure: string;
  chart: ChartKind;
  grain?: string;
  filters?: Record<string, string>;
  limit?: number;
}

export interface DashboardCard {
  title: string;
  hint: string;
  definition: DashboardDefinition;
  /** يشغل عرض اللوحة كاملًا — للاتجاهات الزمنية. */
  wide?: boolean;
  /**
   * لقطة للحالة الراهنة لا لمدة.
   *
   * خط الأنابيب المفتوح الآن لا معنى لتقييده بآخر ثلاثين يومًا: الصفقة
   * المفتوحة منذ سنة ما زالت مفتوحة، وحجبها يُظهر خط أنابيب أفرغ مما هو.
   */
  ignoreRange?: boolean;
}

export const SALES_DASHBOARD: readonly DashboardCard[] = [
  {
    title: 'الإيراد المفوتر شهريًا',
    hint: 'اتجاه الفوترة عبر الأشهر — الخط الصاعد أو الهابط قبل تفسيره.',
    wide: true,
    definition: {
      source: 'INVOICES',
      dimension: 'issuedAt',
      measure: 'total',
      chart: 'LINE',
      grain: 'MONTH',
    },
  },
  {
    title: 'خط الأنابيب حسب المرحلة',
    hint: 'أين تتكدّس القيمة المفتوحة الآن.',
    ignoreRange: true,
    definition: {
      source: 'DEALS',
      dimension: 'stage',
      measure: 'value',
      chart: 'BAR',
      filters: { result: 'OPEN' },
    },
  },
  {
    title: 'الصفقات المكسوبة',
    hint: 'ما أُغلق بالفعل في المدة المختارة.',
    definition: {
      source: 'DEALS',
      dimension: 'closedAt',
      measure: 'value',
      chart: 'BAR',
      grain: 'MONTH',
      filters: { result: 'WON' },
    },
  },
  {
    title: 'أسباب الخسارة',
    hint: 'ما يتكرر فقدانه — وهو أصدق ما في التقرير.',
    definition: {
      source: 'DEALS',
      dimension: 'lostReason',
      measure: 'value',
      chart: 'PIE',
      filters: { result: 'LOST' },
    },
  },
  {
    title: 'العملاء المحتملون حسب المصدر',
    hint: 'أي قناة تجلب أكثر.',
    definition: { source: 'LEADS', dimension: 'source', measure: 'count', chart: 'BAR' },
  },
  {
    title: 'أكبر العملاء فوترةً',
    hint: 'من يحمل الإيراد — وما حجم الاعتماد عليه.',
    definition: {
      source: 'INVOICES',
      dimension: 'account',
      measure: 'total',
      chart: 'BAR',
      limit: 10,
    },
  },
] as const;

export const TEAM_DASHBOARD: readonly DashboardCard[] = [
  {
    title: 'الصفقات المكسوبة حسب المسؤول',
    hint: 'من أغلق، وبكم.',
    wide: true,
    definition: {
      source: 'DEALS',
      dimension: 'owner',
      measure: 'value',
      chart: 'BAR',
      filters: { result: 'WON' },
    },
  },
  {
    title: 'خط الأنابيب حسب المسؤول',
    hint: 'ما يحمله كل واحد من قيمة مفتوحة.',
    ignoreRange: true,
    definition: {
      source: 'DEALS',
      dimension: 'owner',
      measure: 'value',
      chart: 'BAR',
      filters: { result: 'OPEN' },
    },
  },
  {
    title: 'العملاء المحتملون حسب المسؤول',
    hint: 'توزيع الأحمال — لا الأداء.',
    definition: { source: 'LEADS', dimension: 'owner', measure: 'count', chart: 'BAR' },
  },
  {
    title: 'الأنشطة حسب المكلَّف',
    hint: 'المكالمات والمهام والاجتماعات المسجَّلة.',
    definition: {
      source: 'ACTIVITIES',
      dimension: 'assigneeId',
      measure: 'count',
      chart: 'BAR',
    },
  },
  {
    title: 'الصفقات المفقودة حسب المسؤول',
    hint: 'يُقرأ مع المكسوب لا وحده.',
    definition: {
      source: 'DEALS',
      dimension: 'owner',
      measure: 'value',
      chart: 'BAR',
      filters: { result: 'LOST' },
    },
  },
  {
    title: 'عروض الأسعار حسب المسؤول',
    hint: 'ما أُصدر من عروض في المدة.',
    definition: { source: 'QUOTATIONS', dimension: 'owner', measure: 'total', chart: 'BAR' },
  },
] as const;

export const CRM_DASHBOARD: readonly DashboardCard[] = [
  {
    title: 'العملاء المحتملون الجدد شهريًا',
    hint: 'كم يدخل القمعَ من أعلاه كلَّ شهر.',
    wide: true,
    definition: {
      source: 'LEADS',
      dimension: 'createdAt',
      measure: 'count',
      chart: 'LINE',
      grain: 'MONTH',
    },
  },
  {
    title: 'العملاء المحتملون حسب الحالة',
    hint: 'أين يقف من دخل — لقطةٌ للآن.',
    ignoreRange: true,
    definition: { source: 'LEADS', dimension: 'status', measure: 'count', chart: 'BAR' },
  },
  {
    title: 'مصادر العملاء المحتملين',
    hint: 'أي قناة تجلب أكثر — نسبةً.',
    definition: { source: 'LEADS', dimension: 'source', measure: 'count', chart: 'PIE' },
  },
  {
    title: 'الأنشطة حسب النوع',
    hint: 'مكالماتٌ واجتماعاتٌ ومهامّ — حجمُ العمل الميدانيّ.',
    definition: { source: 'ACTIVITIES', dimension: 'type', measure: 'count', chart: 'BAR' },
  },
  {
    title: 'العملاء حسب النشاط',
    hint: 'أيُّ القطاعات تشتري منك.',
    ignoreRange: true,
    definition: { source: 'ACCOUNTS', dimension: 'industry', measure: 'count', chart: 'BAR', limit: 10 },
  },
] as const;

export const FINANCE_DASHBOARD: readonly DashboardCard[] = [
  {
    title: 'الفوترة شهريًا',
    hint: 'ما صدر من فواتير عبر الأشهر.',
    wide: true,
    definition: {
      source: 'INVOICES',
      dimension: 'issuedAt',
      measure: 'total',
      chart: 'LINE',
      grain: 'MONTH',
    },
  },
  {
    title: 'التحصيلات شهريًا',
    hint: 'ما دخل فعلًا — لا ما فُوتر.',
    definition: {
      source: 'PAYMENTS',
      dimension: 'paidAt',
      measure: 'amount',
      chart: 'BAR',
      grain: 'MONTH',
    },
  },
  {
    title: 'الفواتير حسب الحالة',
    hint: 'ما حُصِّل وما تأخّر — لقطةٌ للآن.',
    ignoreRange: true,
    definition: { source: 'INVOICES', dimension: 'status', measure: 'total', chart: 'PIE' },
  },
  {
    title: 'المصروفات حسب البند',
    hint: 'أين يذهب المال.',
    definition: {
      source: 'EXPENSES',
      dimension: 'ledgerAccount',
      measure: 'amount',
      chart: 'BAR',
      limit: 10,
    },
  },
  {
    title: 'فواتير الموردين حسب المورّد',
    hint: 'على من يتركّز ما عليك.',
    definition: { source: 'BILLS', dimension: 'supplier', measure: 'total', chart: 'BAR', limit: 10 },
  },
  {
    title: 'التحصيل حسب الطريقة',
    hint: 'نقدٌ أم تحويلٌ أم شيك.',
    definition: { source: 'PAYMENTS', dimension: 'method', measure: 'amount', chart: 'PIE' },
  },
] as const;

/**
 * لوحةُ النظام في الرئيسية: نظرةٌ على الشركة كلِّها لا على وحدة.
 * بطاقةٌ أو اثنتان من كلّ وحدة — والتفاصيلُ في لوحة الوحدة.
 */
export const HOME_DASHBOARD: readonly DashboardCard[] = [
  SALES_DASHBOARD[0]!,
  SALES_DASHBOARD[1]!,
  CRM_DASHBOARD[2]!,
  FINANCE_DASHBOARD[1]!,
  FINANCE_DASHBOARD[3]!,
  TEAM_DASHBOARD[0]!,
] as const;

export const DASHBOARDS = {
  sales: { title: 'أداء المبيعات', subtitle: 'ما دخل، وما في الطريق، وما فُقد', cards: SALES_DASHBOARD },
  team: { title: 'أداء الفريق', subtitle: 'من يحمل ماذا، ومن أغلق ماذا', cards: TEAM_DASHBOARD },
} as const;

export type DashboardKey = keyof typeof DASHBOARDS;

/**
 * الوحداتُ التي تملك لوحةً.
 *
 * لكلّ وحدةٍ بطاقاتٌ جاهزة (قد تكون صفرًا) وتقاريرُ ينشرها الفريق عليها من
 * الباني. والوحداتُ التي لها تحليلاتٌ خاصّة (الموارد البشرية، مركز
 * الاتصال، المشاريع) تُشير إليها من لوحتها بدل تكرارها.
 */
export const REPORT_MODULES = ['crm', 'sales', 'callcenter', 'agency', 'finance', 'hr'] as const;
export type ReportModule = (typeof REPORT_MODULES)[number];

export interface ReportModuleDef {
  key: ReportModule;
  label: string;
  subtitle: string;
  /** مسارُ لوحة الوحدة في الواجهة. */
  path: string;
  cards: readonly DashboardCard[];
  /** تحليلاتٌ خاصّةٌ بالوحدة خارج محرّك التقارير — تُعرض روابطَ. */
  analytics?: readonly { path: string; label: string }[];
}

export const REPORT_MODULE_DEFS: readonly ReportModuleDef[] = [
  {
    key: 'crm',
    label: 'إدارة العملاء',
    subtitle: 'من يدخل القمع، ومن أين، وما يُنجز معه',
    path: '/crm/dashboard',
    cards: CRM_DASHBOARD,
    analytics: [{ path: '/crm/war-room', label: 'غرفة العمليات' }],
  },
  {
    key: 'sales',
    label: 'المبيعات',
    subtitle: 'ما دخل، وما في الطريق، وما فُقد — ومن أغلق ماذا',
    path: '/sales/dashboard',
    cards: [...SALES_DASHBOARD, ...TEAM_DASHBOARD],
    analytics: [
      { path: '/sales/pipeline', label: 'قمع المبيعات' },
      { path: '/sales/lost', label: 'تحليل الخسائر' },
      { path: '/sales/team', label: 'فريق المبيعات' },
    ],
  },
  {
    key: 'callcenter',
    label: 'مركز الاتصال',
    subtitle: 'المكالمات والجودة والحضور',
    path: '/callcenter/insights',
    cards: [],
    analytics: [
      { path: '/callcenter/dashboard', label: 'لوحة الأداء' },
      { path: '/callcenter/quality', label: 'جودة المكالمات' },
    ],
  },
  {
    key: 'agency',
    label: 'إدارة المشاريع',
    subtitle: 'المشاريع والمهامّ والأهداف',
    path: '/agency/dashboard',
    cards: [],
    analytics: [
      { path: '/agency', label: 'نظرة عامة' },
      { path: '/agency/goals', label: 'الأهداف' },
    ],
  },
  {
    key: 'finance',
    label: 'الحسابات',
    subtitle: 'ما فُوتر وما حُصِّل وما صُرف',
    path: '/finance/dashboard',
    cards: FINANCE_DASHBOARD,
    analytics: [
      { path: '/finance/aging', label: 'تقادم الذمم' },
      { path: '/finance/reports', label: 'القوائم المالية' },
      { path: '/finance/credit', label: 'متابعة الائتمان' },
    ],
  },
  {
    key: 'hr',
    label: 'الموارد البشرية',
    subtitle: 'العاملون والحضور والرواتب',
    path: '/hr/dashboard',
    cards: [],
    analytics: [{ path: '/hr/analytics', label: 'لوحة المؤشّرات' }],
  },
];

export const REPORT_MODULE_MAP = Object.fromEntries(
  REPORT_MODULE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ReportModule, ReportModuleDef>>;

/* ==========================================================================
   مؤشّراتُ الرئيسية — أرقامٌ كبيرةٌ بنسبة تغيّرٍ عن الفترة السابقة
   ========================================================================== */

/**
 * كروتُ المؤشّرات في أعلى الرئيسية — كلٌّ يحمل رقمًا كبيرًا وسهمَ اتجاهٍ
 * ونسبةَ تغيّرٍ عن الشهر السابق (كما في لوحات المؤشّرات المعروفة).
 *
 * كلُّ مؤشّرٍ تعريفُ تقريرٍ يمرّ على المحرّك نفسه مرّتين — هذا الشهر
 * والذي قبله — فالرقمُ صادقٌ لا مُختلق، ونسبةُ التغيّر محسوبةٌ لا مكتوبة.
 * والمصدرُ يُختار بحيث يوافق عمودُ تاريخه معنى «جديدٌ في الفترة»: الفواتيرُ
 * بتاريخ إصدارها، والتحصيلاتُ بتاريخ سدادها، والعملاءُ بتاريخ إنشائهم.
 */
export type HomeKpiGroup = 'sales' | 'clients';

export const HOME_KPI_GROUPS: readonly { key: HomeKpiGroup; label: string }[] = [
  { key: 'sales', label: 'المبيعات' },
  { key: 'clients', label: 'العملاء' },
];

export interface HomeKpiDef {
  key: string;
  label: string;
  group: HomeKpiGroup;
  source: string;
  dimension: string;
  measure: string;
  filters?: Record<string, string>;
  format: ValueFormat;
  /** الوحدةُ التي يخصّها — لإخفائه عمّن لا يملكها. */
  module: ReportModule;
}

export const HOME_KPIS: readonly HomeKpiDef[] = [
  {
    key: 'revenue',
    label: 'الإيراد المفوتر',
    group: 'sales',
    source: 'INVOICES',
    dimension: 'status',
    measure: 'total',
    format: 'MONEY',
    module: 'finance',
  },
  {
    key: 'collected',
    label: 'المحصّل',
    group: 'sales',
    source: 'PAYMENTS',
    dimension: 'method',
    measure: 'amount',
    format: 'MONEY',
    module: 'finance',
  },
  {
    key: 'newCustomers',
    label: 'عملاء جدد',
    group: 'clients',
    source: 'ACCOUNTS',
    dimension: 'industry',
    measure: 'count',
    format: 'NUMBER',
    module: 'crm',
  },
  {
    key: 'newLeads',
    label: 'عملاء محتملون جدد',
    group: 'clients',
    source: 'LEADS',
    dimension: 'source',
    measure: 'count',
    format: 'NUMBER',
    module: 'crm',
  },
];

/** مؤشّرٌ محسوبٌ: قيمةُ الفترة، وقيمةُ ما قبلها، ونسبةُ التغيّر. */
export interface HomeKpi {
  key: string;
  label: string;
  group: HomeKpiGroup;
  format: ValueFormat;
  module: ReportModule;
  value: number;
  previous: number;
  /** نسبةُ التغيّر عن الفترة السابقة، أو null إن كانت السابقةُ صفرًا. */
  delta: number | null;
}

/** مفتاحُ الوحدة في الباقة لكلّ وحدةِ تقارير. */
export const REPORT_MODULE_FEATURE: Readonly<Record<ReportModule, string>> = {
  crm: 'crm',
  sales: 'sales',
  callcenter: 'crm',
  agency: 'crm',
  finance: 'finance',
  hr: 'hr',
};

/** كل البطاقات في مكان واحد — يستعملها الاختبار الذي يتحقق من مفاتيحها. */
export const ALL_DASHBOARD_CARDS: readonly DashboardCard[] = [
  ...SALES_DASHBOARD,
  ...TEAM_DASHBOARD,
  ...CRM_DASHBOARD,
  ...FINANCE_DASHBOARD,
];
